import { useTranslation } from 'react-i18next';
import { useState, FormEvent, ChangeEvent } from 'react';
import { i18n, i18nDynamic } from 'shared/i18n';
import i18next from 'shared/i18n/i18next';
import { useUser } from 'app/state/contexts/UserContext';
import { InfoBlock, InfoBlockCheckBox, LanguageIcon, Modal } from 'app/components';
import config from 'config';
import { langs } from 'shared/forms/LanguageSelector';
import { useUpdateProfile } from 'entities/user';

const UserProperties = () => {
  const { t } = useTranslation(['common', 'lang', 'my-profile']);

  const { user } = useUser();
  const [showModal, setShowModal] = useState(false);
  const [selectedLang, setSelectedLang] = useState(user?.userProperties?.cultureId || config.defaultLocale);
  const [firstName, setFirstName] = useState(user?.firstName);
  const [lastName, setLasttName] = useState(user?.lastName);
  const [mobilePhone, setMobilePhone] = useState(user?.mobilePhone ?? '');
  const [spellCheck, setSpellCheck] = useState<boolean>((user?.userProperties?.spellCheck as boolean | undefined) || false);
  const [displayName, setDisplayName] = useState(`${user?.firstName} ${user?.lastName}`);
  const [displayPhone, setDisplayPhone] = useState(user?.mobilePhone ?? '');
  const [displayLanguage, setDisplayLanguage] = useState(user?.userProperties?.cultureId || config.defaultLocale);
  const { mutate: updateProfile, isPending: busy } = useUpdateProfile();

  const handleClickEdit = () => {
    setShowModal(true);
  };

  const handleClickCancel = () => {
    setShowModal(false);
  };

  const handleCheckboxChange = (isChecked: boolean) => {
    setSpellCheck(isChecked);
  };

  const handleSubmitForm = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    updateProfile(
      {
        firstName,
        lastName,
        mobilePhone,
        telephone: user?.telephone,
        email: user?.email,
        jobTitle: user?.jobTitle,
        userProperties: { cultureId: selectedLang, spellCheck: spellCheck ? 'true' : 'false' },
      },
      {
        onSuccess: () => {
          i18next.changeLanguage(selectedLang);
          setDisplayName(`${firstName} ${lastName}`);
          setDisplayPhone(mobilePhone);
          setDisplayLanguage(selectedLang);
          setShowModal(false);
        },
      }
    );
  };

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedLang(event.target.value);
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center">
          <h2 className="my-0">{t('my-profile:overview.title')}</h2>
          <button className="btn btn-secondary btn-sm" onClick={handleClickEdit}>
            {t('common:edit')} <i className="ms-2 fa-regular fa-gear" />
          </button>
        </div>
        <InfoBlock caption={t('my-profile:name')} text={displayName} className="border-bottom border-gray-300" />
        <InfoBlock caption={t('my-profile:email')} text={user?.email ?? ''} className="border-bottom border-gray-300" />
        <InfoBlock caption={t('my-profile:phone')} text={displayPhone} className="border-bottom border-gray-300" />
        <InfoBlockCheckBox
          caption={t('my-profile:spellcheck')}
          isChecked={spellCheck}
          handleCheckboxChange={handleCheckboxChange}
          className="border-bottom border-gray-300"
        />
        <InfoBlock caption={t('my-profile:language')}>
          <p className="fs-3 d-flex align-items-center">
            <LanguageIcon language={displayLanguage} className="me-3" />
            {i18nDynamic(`lang:${displayLanguage}`)}
          </p>
        </InfoBlock>
        <Modal titleLabelKey="my-profile:overview.modal.title" show={showModal} onClose={handleClickCancel}>
          <form onSubmit={handleSubmitForm}>
            <div className="mb-4">
              <label htmlFor="firstName" className="form-label">
                {t('my-profile:first-name')}
              </label>
              <input
                type="text"
                id="firstName"
                className="form-control"
                value={firstName}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
              />
            </div>
            <div className="mb-4">
              <label htmlFor="lastName" className="form-label">
                {t('my-profile:last-name')}
              </label>
              <input
                type="text"
                id="lastName"
                className="form-control"
                value={lastName}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setLasttName(e.target.value)}
              />
            </div>
            <div className="mb-4">
              <label htmlFor="mobilePhone" className="form-label">
                {t('my-profile:mobile-phone')}
              </label>
              <input
                type="text"
                id="mobilePhone"
                className="form-control"
                value={mobilePhone}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setMobilePhone(e.target.value)}
              />
            </div>
            <div className="mb-4">
              <label htmlFor="spellCheck" className="form-label">
                {t('my-profile:spell-check')}
              </label>
              <div style={{ display: 'block', marginLeft: '1em' }}>
                <input
                  type="checkbox"
                  id="spellCheck"
                  checked={spellCheck}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleCheckboxChange(e.target.checked)}
                />
              </div>
            </div>
            <div className="mb-4">
              <label htmlFor="lang" className="form-label">
                {t('my-profile:lang')}
              </label>
              <select name="lang" id="lang" className="form-select" value={selectedLang} onChange={handleChange}>
                {langs.map(
                  (lang) =>
                    lang.systemLang && (
                      <option key={lang.lang} value={lang.lang}>
                        {i18n(lang.labelKey)}
                      </option>
                    )
                )}
              </select>
            </div>
            <div className="d-flex justify-content-end">
              <button className="btn btn-default" type="button" onClick={handleClickCancel}>
                {t('common:cancel')}
              </button>
              <button className="btn btn-primary">
                {busy ? <i className="fa-regular fa-spinner fa-spin" /> : null} {t('common:save')}
              </button>
            </div>
          </form>
        </Modal>
      </div>
    </div>
  );
};

export default UserProperties;
